import { HiBolt } from "react-icons/hi2";
import { postMessage } from "../../../core/message";
import { notify } from "../../../utils/toast";
import { PickupTimeFilter } from "../hooks/use-pickup-time-filter";

interface StoreFilterChipsProps {
  pickupTimeFilter: PickupTimeFilter | null;
  hasOpenedPickupTimeFilter: boolean;
  onOpenPickupTimeFilter: () => void;
  onResetPickupTimeFilter: () => void;
  availableFilter: "ALL" | "AVAILABLE" | "PICKUP_NOW";
  setAvailableFilter: (filter: "ALL" | "AVAILABLE" | "PICKUP_NOW") => void;
  hasPickupNowStores: boolean;
  pickupNowCount: number;
  /** 지도 탭 등: 픽업시간 칩 없이 노출 필터 3개만 */
  variant?: "default" | "availabilityOnly";
}

const StoreFilterChips = ({
  pickupTimeFilter,
  hasOpenedPickupTimeFilter,
  onOpenPickupTimeFilter,
  onResetPickupTimeFilter,
  availableFilter,
  setAvailableFilter,
  hasPickupNowStores,
  pickupNowCount,
  variant = "default",
}: StoreFilterChipsProps) => {
  const handleFilterClick = (filter: "ALL" | "AVAILABLE" | "PICKUP_NOW") => {
    postMessage("REQ_HAPTIC_SELECTION", "");
    if (filter === "PICKUP_NOW" && !hasPickupNowStores) {
      notify("현재 바로픽업 가능한 매장이 없어요 😔");
      return;
    }
    localStorage.setItem("availableFilter", filter);
    setAvailableFilter(filter);
  };

  /** 리스트: 밝은 회색 / 지도: 맵 위 대비용 흰 칩 */
  const offList = "bg-gray-100 text-gray-600 hover:bg-gray-200";
  const offChip =
    variant === "availabilityOnly"
      ? "bg-white text-gray-900 shadow-md ring-1 ring-gray-900/15 hover:bg-gray-50"
      : offList;

  const pickupDisabled =
    variant === "availabilityOnly"
      ? "bg-white/95 text-gray-400 shadow-sm ring-1 ring-gray-300 cursor-not-allowed"
      : "bg-gray-200 text-gray-400 cursor-not-allowed";

  const availabilityRow = (
    <div className="flex flex-nowrap justify-start gap-2 flex-shrink-0">
        <div
          className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 ease-in-out flex-shrink-0 whitespace-nowrap ${
            availableFilter === "ALL"
              ? "bg-[#1562fc] text-white shadow-md"
              : offChip
          }`}
          onClick={() => handleFilterClick("ALL")}
        >
          모두
        </div>
        <div
          className={`px-3 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 ease-in-out flex-shrink-0 whitespace-nowrap ${
            availableFilter === "AVAILABLE"
              ? "bg-[#1562fc] text-white shadow-md"
              : offChip
          }`}
          onClick={() => handleFilterClick("AVAILABLE")}
        >
          구매가능
        </div>
        <div
          className={`px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ease-in-out flex items-center gap-0.5 flex-shrink-0 whitespace-nowrap ${
            !hasPickupNowStores
              ? pickupDisabled
              : availableFilter === "PICKUP_NOW"
              ? "bg-gradient-to-r from-[#ff4757] to-[#ff6b35] text-white shadow-lg cursor-pointer"
              : `${offChip} cursor-pointer`
          }`}
          onClick={() => handleFilterClick("PICKUP_NOW")}
        >
          <HiBolt className="w-4 h-4" />
          바로픽업
          {hasPickupNowStores && (
            <span
              className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                availableFilter === "PICKUP_NOW" ? "bg-white/20" : "bg-gray-300"
              }`}
            >
              {pickupNowCount}
            </span>
          )}
        </div>
      </div>
  );

  if (variant === "availabilityOnly") {
    return (
      <div className="flex flex-nowrap items-center justify-start gap-2 overflow-visible">
        {availabilityRow}
      </div>
    );
  }

  return (
    <div className="flex flex-nowrap items-center mt-4 gap-2 py-1 px-4 overflow-x-auto">
      <button
        onClick={() => {
          postMessage("REQ_HAPTIC_SELECTION", "");
          onOpenPickupTimeFilter();
        }}
        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ease-in-out flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap relative ${
          pickupTimeFilter
            ? "bg-[#1562fc] text-white shadow-md"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="flex-shrink-0"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span className="whitespace-nowrap">
          {pickupTimeFilter
            ? `${pickupTimeFilter.startTime}~${pickupTimeFilter.endTime}`
            : "픽업시간"}
        </span>
        {pickupTimeFilter && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              postMessage("REQ_HAPTIC_SELECTION", "");
              onResetPickupTimeFilter();
            }}
            className="ml-1 flex-shrink-0 hover:opacity-80 transition-opacity"
            aria-label="필터 초기화"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
        {!hasOpenedPickupTimeFilter && (
          <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
            NEW
          </span>
        )}
      </button>
      <div className="h-6 w-[1px] bg-gray-300 flex-shrink-0" />
      {availabilityRow}
    </div>
  );
};

export default StoreFilterChips;
